// Regra fiscal do desconto de troca/devolução no grupo de pagamento (pag) da NF-e.
//
// Espelha, no frontend, a função Go BuildInvoicePaymentPlan
// (ERPApi/internal/core/services/invoice_payments.go). É usada APENAS para a
// pré-visualização read-only do grupo pag no formulário de emissão — o backend continua
// sendo a fonte da verdade (para NFs de pedido ele deriva as parcelas do banco).
//
// O pedido de venda pode carregar um crédito de troca/devolução
// (product_replacement_discount_value) que reduz o total de venda, mas NÃO pode aparecer
// como desconto na nota nem deixá-la negativa. O crédito vira UMA parcela do meio de
// pagamento 8 — "Desconto de Devolução de Produtos" (is_product_credit_4_return). A soma
// das parcelas tem de bater exatamente com o total da nota (vNF), senão o SEFAZ rejeita.
//
// Situações (a linha 8 sempre vira a parcela de desconto):
//   1. desconto < total do pedido → pagamento real positivo + parcela tipo 8 = desconto.
//   2. desconto = total do pedido → parcela real zerada ignorada + parcela tipo 8 = vNF.
//   3. desconto > total do pedido → parcela real negativa ignorada + parcela tipo 8 = vNF
//      (limitada ao total da nota, não ao desconto).

const round2 = (v: number): number => Math.round((v + Number.EPSILON) * 100) / 100;

export interface PaymentPlanInstallment {
  value: number;
  /** true quando a parcela usa o método de crédito de troca/devolução (método 8). */
  is_return_credit?: boolean;
}

export interface PaymentPlanLine {
  /** índice da parcela de origem; -1 para a linha de desconto sintetizada. */
  sourceIndex: number;
  value: number;
  /** true na linha "Desconto de Devolução de Produtos" (método 8 / tPag 99). */
  isReturnDiscount: boolean;
}

/**
 * Converte as parcelas do pedido nas linhas do grupo pag da NF-e, aplicando a regra do
 * desconto de troca/devolução. Descarta a parcela de crédito e as reais não-positivas;
 * quando replacementDiscount > 0, sintetiza a parcela de desconto com
 * value = round2(invoiceTotal - somaReais), limitada a [0, replacementDiscount].
 */
export function buildInvoicePaymentPlan(
  installments: PaymentPlanInstallment[],
  replacementDiscount: number,
  invoiceTotal: number,
): PaymentPlanLine[] {
  const disc0 = round2(replacementDiscount);
  const total = round2(invoiceTotal);

  const lines: PaymentPlanLine[] = [];
  let realSum = 0;
  installments.forEach((inst, i) => {
    if (inst.is_return_credit) return; // reconstruída como a linha de desconto abaixo
    const v = round2(inst.value);
    if (v <= 0) return; // parcela zerada (sit. 2) ou negativa (sit. 3): ignorar
    lines.push({ sourceIndex: i, value: v, isReturnDiscount: false });
    realSum = round2(realSum + v);
  });

  if (disc0 > 0) {
    let disc = round2(total - realSum);
    if (disc > disc0) disc = disc0; // defensivo: nunca exceder o desconto real
    if (disc > 0) {
      lines.push({ sourceIndex: -1, value: disc, isReturnDiscount: true });
    }
  }

  return lines;
}

/**
 * Frete efetivamente cobrado do cliente na NF-e de um pedido: ShippingPrice −
 * FreightDiscountValue. Espelha domain.OrderTotalsInput.EffectiveShipping do backend
 * (ERPApi/internal/core/domain/order_totals.go) — a MESMA base que compõe o OrderSellTotal
 * e, logo, as parcelas. Frete grátis total (desconto == frete) resulta em 0. O guard
 * (frete >= desconto) espelha o SQL histórico: com desconto > frete usa-se o frete cheio.
 *
 * IMPORTANTE: para NFs de pedido o backend SEMPRE deriva o vFrete assim e ignora o valor
 * enviado pela tela; este helper só mantém o campo exibido coerente com o que será emitido.
 */
export function effectiveOrderFreight(shippingPrice: number, freightDiscountValue: number): number {
  const ship = shippingPrice || 0;
  const disc = freightDiscountValue || 0;
  return ship >= disc ? round2(ship - disc) : round2(ship);
}
